import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from '../../../components/Button';
import Modal from '../../../components/Modal';
import Input from '../../../components/Input';
import Dropdown from '../../../components/Dropdown';
import StatusBadge from '../../../components/StatusBadge';
import RowActions from '../../../components/RowActions';
import Pagination from '../../../components/TableBox/Pagination';
import LoadingRow from '../../../components/TableBox/Rows/LoadingRow';
import Toast from '../../../components/Toast';
import ErasureConfirmModal from '../../../components/ErasureConfirmModal';
import ComplianceService, { DsarRequestRow, DsarRequestType, DsarStatus } from '../../../services/compliance.service';
import TenantService from '../../../services/tenant.service';
import { usePermission } from '../../../hooks/usePermission';
import useSorting from '../../../hooks/useSorting';
import { sortData } from '../../../utils/sortUtils';
import { SortOrder } from '../../../enums';

interface TenantOption {
  id: number;
  tenant_name: string;
}

const REQUEST_TYPE_OPTIONS: DsarRequestType[] = ['access', 'export', 'erasure'];
const STATUS_OPTIONS: DsarStatus[] = ['open', 'in_progress', 'completed', 'closed'];

const STATUS_COLOUR: Record<DsarStatus, string> = {
  open: '#f59e0b',
  in_progress: '#3b82f6',
  completed: '#10b981',
  closed: '#6b7280',
};

const EXPORT_POLL_MS = 10_000;

function downloadBlob(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

const DSARList: React.FC = () => {
  const { t } = useTranslation();
  const canManage = usePermission('compliance:manage');

  const { order, orderBy, setOrder, setOrderBy } = useSorting({ defaultOrder: SortOrder.desc, defaultOrderBy: 'id' });
  const [items, setItems] = useState<DsarRequestRow[]>([]);
  const sortedItems = useMemo(() => sortData(items, orderBy, order), [items, orderBy, order]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [tenantFilter, setTenantFilter] = useState('');
  const [requestTypeFilter, setRequestTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tenants, setTenants] = useState<TenantOption[]>([]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState({
    subject_name: '',
    subject_email: '',
    tenant_id: '',
    request_date: new Date().toISOString().slice(0, 10),
    request_type: 'access' as DsarRequestType,
    notes: '',
  });
  const [creating, setCreating] = useState(false);

  const [compilingId, setCompilingId] = useState<number | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [erasureTarget, setErasureTarget] = useState<DsarRequestRow | null>(null);

  const [complianceExport, setComplianceExport] = useState<any>(null);
  const [triggeringExport, setTriggeringExport] = useState(false);
  const [downloadingExport, setDownloadingExport] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    ComplianceService.list({
      tenantId: tenantFilter ? Number(tenantFilter) : undefined,
      requestType: (requestTypeFilter || undefined) as DsarRequestType | undefined,
      status: (statusFilter || undefined) as DsarStatus | undefined,
      limit: 100,
    })
      .then((response: any) => setItems(response?.data?.items ?? []))
      .finally(() => setLoading(false));
  }, [tenantFilter, requestTypeFilter, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    TenantService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setTenants(response?.data?.items ?? []);
    });
  }, []);

  const loadExportStatus = useCallback(() => {
    if (!tenantFilter || !canManage) {
      setComplianceExport(null);
      return;
    }
    ComplianceService.getComplianceExportStatus(tenantFilter).then((response: any) => setComplianceExport(response?.data ?? null));
  }, [tenantFilter, canManage]);

  useEffect(() => {
    loadExportStatus();
  }, [loadExportStatus]);

  useEffect(() => {
    const isActive = complianceExport && ['queued', 'running'].includes(complianceExport.status);
    if (!isActive) return undefined;
    const interval = setInterval(loadExportStatus, EXPORT_POLL_MS);
    return () => clearInterval(interval);
  }, [complianceExport, loadExportStatus]);

  const handleCreate = useCallback(async () => {
    setCreating(true);
    try {
      await ComplianceService.create({
        subject_name: form.subject_name,
        subject_email: form.subject_email,
        tenant_id: Number(form.tenant_id),
        request_date: form.request_date,
        request_type: form.request_type,
        notes: form.notes || undefined,
      });
      toast.success(<Toast message={t('compliance.dsar.pageTexts.createSuccess')} />, { icon: false });
      setShowCreateModal(false);
      setForm({
        subject_name: '',
        subject_email: '',
        tenant_id: '',
        request_date: new Date().toISOString().slice(0, 10),
        request_type: 'access',
        notes: '',
      });
      load();
    } finally {
      setCreating(false);
    }
  }, [form, load, t]);

  const handleCompile = useCallback(
    async (id: number) => {
      setCompilingId(id);
      try {
        await ComplianceService.compile(id);
        toast.success(<Toast message={t('compliance.dsar.pageTexts.compileSuccess')} />, { icon: false });
        load();
      } finally {
        setCompilingId(null);
      }
    },
    [load, t]
  );

  const handleDownload = useCallback(async (row: DsarRequestRow) => {
    setDownloadingId(row.id);
    try {
      const response: any = await ComplianceService.downloadCompiled(row.id);
      const blob = response?.data instanceof Blob ? response.data : new Blob([response?.data]);
      downloadBlob(blob, `dsar-${row.subject_email}.json`);
    } finally {
      setDownloadingId(null);
    }
  }, []);

  const handleStatusChange = useCallback(
    async (id: number, status: DsarStatus) => {
      await ComplianceService.updateStatus(id, status);
      load();
    },
    [load]
  );

  const handleTriggerExport = useCallback(async () => {
    if (!tenantFilter) return;
    setTriggeringExport(true);
    try {
      await ComplianceService.triggerComplianceExport(tenantFilter);
      toast.success(<Toast message={t('compliance.dsar.pageTexts.export.started')} />, { icon: false });
      loadExportStatus();
    } finally {
      setTriggeringExport(false);
    }
  }, [tenantFilter, loadExportStatus, t]);

  const handleDownloadExport = useCallback(async () => {
    if (!tenantFilter) return;
    setDownloadingExport(true);
    try {
      const response: any = await ComplianceService.downloadComplianceExport(tenantFilter);
      const blob = response?.data instanceof Blob ? response.data : new Blob([response?.data]);
      downloadBlob(blob, `tenant-${tenantFilter}-compliance-package.zip`);
    } finally {
      setDownloadingExport(false);
    }
  }, [tenantFilter]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h4 className="m-0">{t('compliance.dsar.pageTexts.title')}</h4>
          <p className="text-muted m-0">{t('compliance.dsar.pageTexts.subTitle')}</p>
        </div>
        {canManage && (
          <Button className="btn btn-primary" text={t('compliance.dsar.pageTexts.createNew')} onClick={() => setShowCreateModal(true)} />
        )}
      </div>

      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <Dropdown
            name="tenantFilter"
            value={tenantFilter}
            placeholder={t('compliance.dsar.pageTexts.filters.allTenants')}
            options={tenants.map((tenant) => ({ id: String(tenant.id), name: tenant.tenant_name }))}
            noSearch
            onChange={(e) => setTenantFilter(e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <Dropdown
            name="requestTypeFilter"
            value={requestTypeFilter}
            placeholder={t('compliance.dsar.pageTexts.filters.allRequestTypes')}
            options={REQUEST_TYPE_OPTIONS.map((type) => ({ id: type, name: t(`compliance.dsar.pageTexts.requestTypes.${type}`) }))}
            noSearch
            onChange={(e) => setRequestTypeFilter(e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <Dropdown
            name="statusFilter"
            value={statusFilter}
            placeholder={t('compliance.dsar.pageTexts.filters.allStatuses')}
            options={STATUS_OPTIONS.map((status) => ({ id: status, name: t(`compliance.dsar.pageTexts.statuses.${status}`) }))}
            noSearch
            onChange={(e) => setStatusFilter(e.target.value)}
          />
        </div>
      </div>

      {canManage && tenantFilter && (
        <div className="alert alert-light border d-flex justify-content-between align-items-center mb-3">
          <span>
            {t('compliance.dsar.pageTexts.export.title')}
            {complianceExport && (
              <>
                {' — '}
                <strong>{complianceExport.status}</strong>
                {complianceExport.status === 'failed' && complianceExport.error_message && ` — ${complianceExport.error_message}`}
              </>
            )}
          </span>
          <div className="d-flex gap-2">
            {complianceExport?.status === 'ready' && (
              <Button
                className="btn btn-sm btn-primary"
                text={t('compliance.dsar.pageTexts.export.download')}
                loading={downloadingExport}
                onClick={handleDownloadExport}
              />
            )}
            <Button
              className="btn btn-sm btn-outline-primary"
              text={
                complianceExport?.status === 'failed'
                  ? t('compliance.dsar.pageTexts.export.retry')
                  : t('compliance.dsar.pageTexts.export.trigger')
              }
              loading={triggeringExport}
              disabled={complianceExport && ['queued', 'running'].includes(complianceExport.status)}
              onClick={handleTriggerExport}
            />
          </div>
        </div>
      )}

      <div className="obs-table-card card">
        <div className="card-body p-0" style={{ overflowX: 'auto' }}>
          <table className="table obs-table mb-0">
            <thead>
              <tr>
                {[
                  { id: 'subject_name', label: t('compliance.dsar.pageTexts.table.columns.subjectName') },
                  { id: 'tenant_name', label: t('compliance.dsar.pageTexts.table.columns.tenant') },
                  { id: 'request_type', label: t('compliance.dsar.pageTexts.table.columns.requestType') },
                  { id: 'status', label: t('compliance.dsar.pageTexts.table.columns.status'), style: { width: '160px', minWidth: '160px' } },
                  { id: 'created_at', label: t('compliance.dsar.pageTexts.table.columns.createdAt'), style: { whiteSpace: 'nowrap' } },
                  { id: 'completed_at', label: t('compliance.dsar.pageTexts.table.columns.completedAt'), style: { whiteSpace: 'nowrap' } },
                ].map((col) => {
                  const isSorted = orderBy === col.id;
                  return (
                    <th
                      key={col.id}
                      className={`sorting ${isSorted ? (order === SortOrder.asc ? 'sorting_asc' : 'sorting_desc') : ''}`}
                      style={{ cursor: 'pointer', userSelect: 'none', ...col.style }}
                      onClick={() => {
                        if (orderBy === col.id) {
                          setOrder(order === SortOrder.asc ? SortOrder.desc : SortOrder.asc);
                        } else {
                          setOrderBy(col.id);
                          setOrder(SortOrder.asc);
                        }
                      }}
                    >
                      <span className="d-inline-flex align-items-center gap-1 w-100">
                        <span>{col.label}</span>
                        <span style={{ opacity: isSorted ? 1 : 0.4, transition: 'opacity 0.15s ease' }}>
                          {isSorted ? (
                            order === SortOrder.asc ? (
                              <i className="ri-arrow-up-s-line" style={{ fontSize: '13px', fontWeight: 600 }} />
                            ) : (
                              <i className="ri-arrow-down-s-line" style={{ fontSize: '13px', fontWeight: 600 }} />
                            )
                          ) : (
                            <i className="ri-arrow-up-down-line" style={{ fontSize: '13px' }} />
                          )}
                        </span>
                      </span>
                    </th>
                  );
                })}
                <th className="text-end">{t('compliance.dsar.pageTexts.table.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <LoadingRow colSpan={7} />
              ) : sortedItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-muted text-center py-4">{t('compliance.dsar.pageTexts.table.noDataFoundMessage')}</td>
                </tr>
              ) : (
                sortedItems.slice((page - 1) * perPage, (page - 1) * perPage + perPage).map((row) => (
                  <tr key={row.id}>
                    <td>{row.subject_name}</td>
                    <td>{row.tenant_name ?? '—'}</td>
                    <td>
                      <StatusBadge label={t(`compliance.dsar.pageTexts.requestTypes.${row.request_type}`)} colour="#6366f1" />
                    </td>
                    <td style={{ width: '160px', minWidth: '160px', verticalAlign: 'middle' }}>
                      {canManage ? (
                        <Dropdown
                          name={`status-${row.id}`}
                          value={row.status}
                          options={STATUS_OPTIONS.map((status) => ({ id: status, name: t(`compliance.dsar.pageTexts.statuses.${status}`) }))}
                          noSearch
                          hideClearIcon
                          compact
                          onChange={(e) => handleStatusChange(row.id, e.target.value as DsarStatus)}
                        />
                      ) : (
                        <StatusBadge label={t(`compliance.dsar.pageTexts.statuses.${row.status}`)} colour={STATUS_COLOUR[row.status]} />
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{new Date(row.created_at).toLocaleDateString()}</td>
                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{row.completed_at ? new Date(row.completed_at).toLocaleDateString() : '—'}</td>
                    <td className="text-end" style={{ verticalAlign: 'middle' }}>
                      {canManage && row.request_type !== 'erasure' && (
                        <RowActions
                          actions={[
                            row.compiled_data_path && row.compiled_data_expires_at && new Date(row.compiled_data_expires_at).getTime() > Date.now()
                              ? {
                                  id: 'download',
                                  label: t('compliance.dsar.pageTexts.download') || 'Download Package',
                                  icon: <i className="ri-download-2-line" />,
                                  onClick: () => handleDownload(row),
                                }
                              : {
                                  id: 'compile',
                                  label: t('compliance.dsar.pageTexts.compile') || 'Compile Package',
                                  icon: <i className="ri-refresh-line" />,
                                  onClick: () => handleCompile(row.id),
                                },
                          ]}
                        />
                      )}
                      {canManage && row.request_type === 'erasure' && row.status !== 'completed' && (
                        <Button
                          className="btn btn-sm btn-danger"
                          text={t('compliance.dsar.pageTexts.erasure.initiate')}
                          onClick={() => setErasureTarget(row)}
                        />
                      )}
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
        </div>
        <Pagination
          page={page}
          total={items.length}
          perPage={perPage}
          onPageChange={(p) => setPage(p)}
          onPerPageChange={(s) => {
            setPerPage(s);
            setPage(1);
          }}
        />
      </div>

      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title={t('compliance.dsar.pageTexts.createTitle')}
        content={
          <div className="row g-3">
            <div className="col-12">
              <Input
                id="subjectName"
                name="subjectName"
                label={t('compliance.dsar.pageTexts.fields.subjectName')}
                required
                value={form.subject_name}
                onChange={(e) => setForm((f) => ({ ...f, subject_name: e.target.value }))}
              />
            </div>
            <div className="col-12">
              <Input
                id="subjectEmail"
                name="subjectEmail"
                label={t('compliance.dsar.pageTexts.fields.subjectEmail')}
                required
                value={form.subject_email}
                onChange={(e) => setForm((f) => ({ ...f, subject_email: e.target.value }))}
              />
            </div>
            <div className="col-12">
              <Dropdown
                name="tenant_id"
                label={t('compliance.dsar.pageTexts.fields.tenant')}
                required
                value={form.tenant_id}
                placeholder={t('compliance.dsar.pageTexts.filters.allTenants')}
                options={tenants.map((tenant) => ({ id: String(tenant.id), name: tenant.tenant_name }))}
                noSearch
                onChange={(e) => setForm((f) => ({ ...f, tenant_id: e.target.value }))}
              />
            </div>
            <div className="col-12">
              <Input
                id="requestDate"
                name="requestDate"
                type="date"
                label={t('compliance.dsar.pageTexts.fields.requestDate')}
                required
                value={form.request_date}
                onChange={(e) => setForm((f) => ({ ...f, request_date: e.target.value }))}
              />
            </div>
            <div className="col-12">
              <Dropdown
                name="request_type"
                label={t('compliance.dsar.pageTexts.fields.requestType')}
                required
                value={form.request_type}
                options={REQUEST_TYPE_OPTIONS.map((type) => ({ id: type, name: t(`compliance.dsar.pageTexts.requestTypes.${type}`) }))}
                noSearch
                onChange={(e) => setForm((f) => ({ ...f, request_type: e.target.value as DsarRequestType }))}
              />
            </div>
            <div className="col-12">
              <Input
                id="notes"
                name="notes"
                label={t('compliance.dsar.pageTexts.fields.notes')}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>
        }
        action={
          <>
            <Button className="btn btn-outline-secondary" text={t('compliance.dsar.pageTexts.cancel')} onClick={() => setShowCreateModal(false)} />
            <Button
              className="btn btn-primary"
              text={t('compliance.dsar.pageTexts.save')}
              loading={creating}
              disabled={!form.subject_name.trim() || !form.subject_email.trim() || !form.tenant_id || !form.request_date}
              onClick={handleCreate}
            />
          </>
        }
      />

      {erasureTarget && (
        <ErasureConfirmModal
          open={!!erasureTarget}
          dsarId={erasureTarget.id}
          subjectEmail={erasureTarget.subject_email}
          onClose={() => setErasureTarget(null)}
          onErased={() => {
            setErasureTarget(null);
            load();
          }}
        />
      )}
    </div>
  );
};

export default DSARList;
